import React, { useEffect, useMemo, useState } from 'react';
import { useLive } from '../../live.jsx';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { formatPrice, formatDate } from '../../format.js';
import { useCategoryTree, nodeName, norm } from '../../categoryTree.js';
import { Alert } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import UpgradeCard from '../../components/UpgradeCard.jsx';
import { AccountHead, useAccount, allowed } from './AccountLayout.jsx';

// Mes produits: every product with photo, name, category path, price,
// status (Brouillon / En révision / Publié / Refusé / Masqué), last update
// and views. Search, filter (status, category), sort; per row: edit
// (wizard), duplicate (similar product), hide / show, delete, view on
// site; bulk hide / show / delete. Deleting asks for confirmation here
// (no browser pop-up).
// awaiting = sent by a team member, waiting for the owner's / an admin's approval.
export const productStatus = (l) => (l.awaitingTeam ? 'awaiting' : l.hidden && l.status !== 'removed' ? 'hidden' : l.status);
const STATUSES = ['approved', 'pending', 'awaiting', 'draft', 'rejected', 'hidden'];
const SORTS = ['newest', 'name', 'status'];

export default function ProductsPage() {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const { summary, reload } = useAccount();
  const tree = useCategoryTree();
  const location = useLocation();
  usePageTitle('accountNav.products');
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(location.state?.saved ? t('myListings.statusHelp.pending') : '');
  const [selected, setSelected] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // { ids, title }
  const [busy, setBusy] = useState(false);
  const q = params.get('q') || '';
  const status = params.get('status') || '';
  const cat = params.get('cat') || '';
  const addedBy = params.get('by') || '';
  const sort = SORTS.includes(params.get('sort')) ? params.get('sort') : 'newest';
  const setParam = (k, v) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n, { replace: true }); };

  const load = () => api('/my/listings').then(setItems).catch((e) => setError(errorText(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLive(['account', 'listings'], () => { load(); }); // approved / returned while open

  const pathOf = (l) => (l.category ? tree.pathText(l.category.id, lang) || nodeName(l.category, lang) : '');
  const cats = useMemo(() => {
    const ids = new Set((items || []).map((l) => l.category?.id).filter(Boolean));
    return [...ids].map((id) => ({ id, label: tree.pathText(id, lang) || nodeName((items || []).find((l) => l.category?.id === id)?.category, lang) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items, tree, lang]);
  const shown = useMemo(() => {
    if (!items) return [];
    const words = norm(q).split(/\s+/).filter(Boolean);
    let list = items.filter((l) => (!status || productStatus(l) === status) && (!addedBy || String(l.addedBy?.id) === addedBy)
      && (!cat || String(l.category?.id) === cat || tree.path(l.category?.id).some((n) => String(n.id) === cat))
      && words.every((w) => norm(`${l.title} ${pathOf(l)}`).includes(w)));
    const order = { pending: 0, rejected: 1, draft: 2, approved: 3, hidden: 4, removed: 5 };
    list = [...list].sort((a, b) => (sort === 'name' ? (a.title || '').localeCompare(b.title || '', lang)
      : sort === 'status' ? order[productStatus(a)] - order[productStatus(b)] || new Date(b.updatedAt) - new Date(a.updatedAt)
        : new Date(b.updatedAt) - new Date(a.updatedAt)));
    return list;
  }, [items, q, status, cat, sort, tree, lang, addedBy]); // eslint-disable-line react-hooks/exhaustive-deps
  // Company users (migration 031): who added each product; approval of a team member's product.
  const canApprove = allowed(user, 'products.all') && allowed(user, 'products.publish');
  const adders = useMemo(() => [...new Map((items || []).filter((l) => l.addedBy).map((l) => [l.addedBy.id, l.addedBy.name])).entries()], [items]);
  const showAdder = (allowed(user, 'products.all') || !allowed(user, 'products.create')) && adders.length > 1;
  const teamApprove = (l) => run(() => api(`/my/listings/${l.id}/team-approve`, { method: 'POST' }), t('productsPage.teamApproved'));
  const teamReturn = (l) => { const note = window.prompt(t('productsPage.returnWhy')); if (note !== null) run(() => api(`/my/listings/${l.id}/team-return`, { method: 'POST', body: { note } }), t('productsPage.teamReturned')); };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = shown.length > 0 && shown.every((l) => selected.has(l.id));
  const say = (m) => { setToast(m); setError(''); };

  async function run(fn, msg) {
    setBusy(true); setError('');
    try { await fn(); await load(); reload(); if (msg) say(msg); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }
  const setHidden = (l, hidden) => run(() => api(`/my/listings/${l.id}/visibility`, { method: 'POST', body: { hidden } }), t(hidden ? 'productsPage.hiddenOne' : 'productsPage.shownOne'));
  const bulk = (action, ids = [...selected]) => run(async () => {
    const r = await api('/my/listings/bulk', { method: 'POST', body: { ids, action } });
    setSelected(new Set()); setConfirm(null);
    setToast(t(`productsPage.bulkDone.${action}`, { n: r.done }));
  });

  const limit = summary?.limit;
  const full = limit && limit.max !== null && limit.used >= limit.max;
  const canAdd = user.isSeller && user.emailVerified && user.profileComplete && !full && allowed(user, 'products.create');

  const actions = (l) => {
    const st = productStatus(l);
    return (
      <div className="acc-actions">
        {l.awaitingTeam && canApprove && <><button type="button" className="btn btn-primary btn-sm" onClick={() => teamApprove(l)} disabled={busy}>{t('productsPage.approve')}</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => teamReturn(l)} disabled={busy}>{t('productsPage.return')}</button></>}
        <Link className="acc-icon-btn" to={`/account/listings/${l.id}/edit`} title={t('productsPage.edit')} aria-label={`${t('productsPage.edit')} — ${l.title}`}><Icon name="edit" size={18} /></Link>
        {user.isSeller && !full && allowed(user, 'products.create') && <Link className="acc-icon-btn" to={`/account/listings/new?from=${l.id}`} title={t('productsPage.duplicate')} aria-label={`${t('productsPage.duplicate')} — ${l.title}`}><Icon name="copy" size={18} /></Link>}
        {st !== 'draft' && (l.hidden
          ? <button type="button" className="acc-icon-btn" onClick={() => setHidden(l, false)} disabled={busy} title={t('productsPage.show')} aria-label={`${t('productsPage.show')} — ${l.title}`}><Icon name="eye" size={18} /></button>
          : <button type="button" className="acc-icon-btn" onClick={() => setHidden(l, true)} disabled={busy} title={t('productsPage.hide')} aria-label={`${t('productsPage.hide')} — ${l.title}`}><Icon name="eyeOff" size={18} /></button>)}
        {st === 'approved' && <a className="acc-icon-btn" href={`/marketplace/${l.id}`} target="_blank" rel="noreferrer" title={t('productsPage.view')} aria-label={`${t('productsPage.view')} — ${l.title}`}><Icon name="external" size={18} /></a>}
        <button type="button" className="acc-icon-btn is-danger" onClick={() => setConfirm({ ids: [l.id], title: l.title })} disabled={busy} title={t('common.delete')} aria-label={`${t('common.delete')} — ${l.title}`}><Icon name="trash" size={18} /></button>
      </div>
    );
  };
  const chip = (l) => <span className={`chip-status chip-${productStatus(l)}`}>{t(`productsPage.status.${productStatus(l)}`)}</span>;
  const price = (l) => (l.title || l.status !== 'draft' ? formatPrice(l, lang, t) : '—');
  const thumb = (l) => (l.photos[0] ? <img className="acc-thumb" src={l.photos[0].url} alt="" loading="lazy" /> : <span className="acc-thumb acc-thumb-empty"><Icon name="box" size={22} /></span>);

  return (
    <div className="acc-page">
      <AccountHead place="ws.products.list" title={t('accountNav.products')} lead={t('productsPage.lead')}
        actions={<>{canAdd && <Link className="btn btn-gold" to="/account/listings/new"><Icon name="plus" size={18} /> {t('accountNav.add')}</Link>}</>} />
      {!user.isSeller && <UpgradeCard reason="sell" />}
      {limit && (
        <div className={`acc-limit${full ? ' is-full' : ''}`}>
          {limit.max !== null ? (
            <>
              <strong>{t('productsPage.limit', { used: limit.used, max: limit.max })}</strong>
              <span className="acc-limit-bar" aria-hidden="true"><span style={{ width: `${Math.min(100, (limit.used / Math.max(1, limit.max)) * 100)}%` }} /></span>
              {full ? <span>{t('productsPage.limitFull')}</span> : <span className="muted">{t('productsPage.limitLeft', { n: limit.max - limit.used })}</span>}
              <Link to="/account/plan">{t('productsPage.upgrade')} →</Link>
            </>
          ) : <span>{t('productsPage.noLimit', { n: limit.used })}</span>}
        </div>
      )}

      <div className="acc-toolbar" role="search">
        <label className="acc-search"><span className="sr-only">{t('productsPage.search')}</span><Icon name="search" size={18} />
          <input type="search" value={q} onChange={(e) => setParam('q', e.target.value)} placeholder={t('productsPage.search')} /></label>
        <label><span className="sr-only">{t('productsPage.filterStatus')}</span>
          <select value={status} onChange={(e) => setParam('status', e.target.value)}>
            <option value="">{t('productsPage.allStatuses')}</option>
            {STATUSES.map((x) => <option key={x} value={x}>{t(`productsPage.status.${x}`)}</option>)}
          </select></label>
        <label><span className="sr-only">{t('productsPage.filterCategory')}</span>
          <select value={cat} onChange={(e) => setParam('cat', e.target.value)}>
            <option value="">{t('productsPage.allCategories')}</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select></label>
        {showAdder && <label><span className="sr-only">{t('productsPage.addedBy')}</span>
          <select value={addedBy} onChange={(e) => setParam('by', e.target.value)}>
            <option value="">{t('productsPage.anyAdder')}</option>
            {adders.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select></label>}
        <label><span className="sr-only">{t('productsPage.sort')}</span>
          <select value={sort} onChange={(e) => setParam('sort', e.target.value === 'newest' ? '' : e.target.value)}>
            {SORTS.map((x) => <option key={x} value={x}>{t(`productsPage.sorts.${x}`)}</option>)}
          </select></label>
      </div>

      <Alert kind="success">{toast}</Alert>
      <Alert>{error}</Alert>

      {selected.size > 0 && !confirm && (
        <div className="acc-bulk" role="region" aria-label={t('productsPage.bulk')}>
          <strong>{t('productsPage.selected', { n: selected.size })}</strong>
          <button type="button" className="btn btn-sm" onClick={() => bulk('hide')} disabled={busy}><Icon name="eyeOff" size={16} /> {t('productsPage.hide')}</button>
          <button type="button" className="btn btn-sm" onClick={() => bulk('show')} disabled={busy}><Icon name="eye" size={16} /> {t('productsPage.show')}</button>
          <button type="button" className="btn btn-sm" onClick={() => setConfirm({ ids: [...selected] })} disabled={busy}><Icon name="trash" size={16} /> {t('common.delete')}</button>
          <button type="button" className="btn btn-sm" onClick={() => setSelected(new Set())}>{t('productsPage.clearSel')}</button>
        </div>
      )}
      {confirm && (
        <div className="acc-confirm" role="alertdialog" aria-labelledby="del-q">
          <p id="del-q"><strong>{confirm.title ? t('productsPage.confirmOne', { title: confirm.title }) : t('productsPage.confirmMany', { n: confirm.ids.length })}</strong> {t('productsPage.confirmHelp')}</p>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirm(null)} autoFocus>{t('common.cancel')}</button>
          <button type="button" className="btn btn-danger btn-sm" onClick={() => bulk('delete', confirm.ids)} disabled={busy}>{t('productsPage.confirmDelete')}</button>
        </div>
      )}

      {!items && !error && <p className="muted">{t('common.loading')}</p>}
      {items && items.length === 0 && (
        <div className="acc-empty"><p className="strong">{t('myListings.empty')}</p><p className="muted">{t('myListings.emptyHint')}</p>
          {canAdd && <Link className="btn btn-gold" to="/account/listings/new">{t('accountNav.add')}</Link>}</div>
      )}
      {items && items.length > 0 && shown.length === 0 && <p className="muted">{t('productsPage.noMatch')}</p>}

      {shown.length > 0 && (
        <>
          <div className="acc-table-wrap">
            <table className="acc-table">
              <thead>
                <tr>
                  <th scope="col"><input type="checkbox" checked={allOn} onChange={() => setSelected(allOn ? new Set() : new Set(shown.map((l) => l.id)))} aria-label={t('productsPage.selectAll')} /></th>
                  <th scope="col"><span className="sr-only">{t('productsPage.photo')}</span></th>
                  <th scope="col">{t('productsPage.cols.product')}</th>
                  <th scope="col">{t('productsPage.cols.price')}</th>
                  <th scope="col">{t('productsPage.cols.status')}</th>
                  <th scope="col">{t('productsPage.cols.updated')}</th>
                  <th scope="col">{t('productsPage.cols.views')}</th>
                  <th scope="col"><span className="sr-only">{t('productsPage.cols.actions')}</span></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((l) => (
                  <tr key={l.id} className={selected.has(l.id) ? 'is-selected' : ''}>
                    <td><input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSel(l.id)} aria-label={t('productsPage.selectOne', { title: l.title || t('myListings.untitled') })} /></td>
                    <td>{thumb(l)}</td>
                    <td>
                      <Link className="acc-prod-name" to={`/account/listings/${l.id}/edit`}>{l.title || t('myListings.untitled')}</Link>
                      <div className="acc-path">{pathOf(l)}</div>
                      {showAdder && l.addedBy && <div className="acc-path">{t('productsPage.addedByName', { name: l.addedBy.name })}</div>}
                      {l.status === 'rejected' && l.rejectionReason && <div className="acc-reason">{t('productsPage.reason', { reason: l.rejectionReason })}</div>}
                    </td>
                    <td className="acc-num">{price(l)}</td>
                    <td>{chip(l)}</td>
                    <td className="acc-num">{formatDate(l.updatedAt, lang)}</td>
                    <td className="acc-num">{l.views}</td>
                    <td>{actions(l)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="acc-cards">
            {shown.map((l) => (
              <li key={l.id} className={`acc-pcard${selected.has(l.id) ? ' is-selected' : ''}`}>
                <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSel(l.id)} aria-label={t('productsPage.selectOne', { title: l.title || t('myListings.untitled') })} />
                {thumb(l)}
                <div>
                  <div className="acc-pcard-top"><Link className="acc-prod-name" to={`/account/listings/${l.id}/edit`}>{l.title || t('myListings.untitled')}</Link>{chip(l)}</div>
                  <div className="acc-path">{pathOf(l)}</div>
                  {showAdder && l.addedBy && <div className="acc-path">{t('productsPage.addedByName', { name: l.addedBy.name })}</div>}
                  <div className="acc-pcard-meta"><span>{price(l)}</span><span>{formatDate(l.updatedAt, lang)}</span><span>{t('productsPage.viewsN', { n: l.views })}</span></div>
                  {l.status === 'rejected' && l.rejectionReason && <div className="acc-reason">{t('productsPage.reason', { reason: l.rejectionReason })}</div>}
                </div>
                {actions(l)}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
